import React from "react";
import {
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";

const useStyles = makeStyles((theme) => ({
    chips: {
        marginTop: theme.spacing(1),
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
    },
    cardChips: {
        color: "#fff",
        background: "darkblue",
        marginTop: theme.spacing(1),
    },
    video: {
        height: 0,
        paddingBottom: "56.25%",
        position: "relative",
        background: "#e9ecef",
        borderRadius: 8,
        marginTop: theme.spacing(1),
    },
    videoLabel: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%,-50%)",
        color: "#6c757d",
    },
}));

const TREE_ENDPOINT = "/api/help/tree";

function findTopicById(catalog, topicId) {
    const walk = (node, path = []) => {
        if (node.topics) {
            for (const t of node.topics) {
                if (t.id === topicId) return { ...t, path };
            }
        }
        for (const child of node.children || []) {
            const found = walk(child, [...path, child.title]);
            if (found) return found;
        }
        return null;
    };

    for (const top of catalog) {
        const found = walk(top, [top.title]);
        if (found) return found;
    }
    return null;
}

/**
 * HelpTopicLink with no navigation:
 * Props:
 *   topicId  {number}:  the numeric ID of the HelpTopic in the database (admin panel shows this ID in the table view)
 *   label    {string}:  visible link text (defaulted to "Help Center")
 *   style    {object}:  optional inline styles applied to the <a> element
 */
export default function HelpTopicLink({ topicId, label = "Help Center", style }) {
    const classes = useStyles();
    const [openTopic, setOpenTopic] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [fetchError, setFetchError] = React.useState(false);

    const handleClick = async (e) => {
        e.preventDefault();
        if (loading) return;
        setFetchError(false);
        setLoading(true);
        try {
            const res = await fetch(TREE_ENDPOINT, { credentials: "same-origin" });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const catalog = await res.json();
            const topic = findTopicById(catalog, topicId);
            if (topic) {
                setOpenTopic(topic);
            } else {
                setFetchError(true);
            }
        } catch {
            setFetchError(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <a
                href="#"
                onClick={handleClick}
                style={{ color: "blue", ...style }}
            >
                {loading ? (
                    <>
                        {label}&nbsp;
                        <CircularProgress size={12} style={{ verticalAlign: "middle" }} />
                    </>
                ) : fetchError ? (
                    `${label} (unavailable)`
                ) : (
                    label
                )}
            </a>

            <Dialog
                open={Boolean(openTopic)}
                onClose={() => setOpenTopic(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>{openTopic?.title}</DialogTitle>

                <DialogContent dividers>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        {openTopic
                            ? ["Help Center", ...(openTopic.path || [])].join(" › ")
                            : ""}
                    </Typography>

                    {openTopic?.video_url ? (
                        <div className={classes.video}>
                            <video
                                controls
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: 8,
                                }}
                            >
                                <source src={openTopic.video_url} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    ) : (
                        <div className={classes.video}>
                            <Typography className={classes.videoLabel}>
                                Video placeholder
                            </Typography>
                        </div>
                    )}

                    <Box mt={2}>
                        <Typography variant="subtitle2">Document</Typography>
                        {openTopic?.document ? (
                            <Box mt={1}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    component="a"
                                    href={openTopic.document}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Open attached document
                                </Button>
                            </Box>
                        ) : openTopic?.body_md ? (
                            <Box mt={1}>
                                <Typography
                                    component="pre"
                                    style={{ whiteSpace: "pre-wrap", margin: 0 }}
                                >
                                    {openTopic.body_md}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography color="textSecondary">
                                No document attached.
                            </Typography>
                        )}
                    </Box>

                    {openTopic?.tags?.length ? (
                        <Box mt={2} className={classes.chips}>
                            {openTopic.tags.map((tag) => (
                                <Chip
                                    key={tag}
                                    size="small"
                                    label={`#${tag}`}
                                    className={classes.cardChips}
                                />
                            ))}
                        </Box>
                    ) : null}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpenTopic(null)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}